import React, {
  FC,
  useMemo,
  useState,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
  useRef
} from "react";

import { endOfDay, startOfDay, sub } from "date-fns";
import { useLocation } from "react-router-dom";
//redux
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store/ducks";
import { Creators as Actions } from "store/ducks/quotesDashboard";
//components
import { VisibilityConditional } from "components/VisibilityConditional";
import { Subtitles } from "../components/generics/Subtitles";
import { Row } from "components/Layout/Grid";
import { GridQuotes } from "./GridQuotes";
import HeaderGrid from "./HeaderGrid";
import QuotePanel from "./QuotePanel";
//models
import { QuoteDashboardIndicator } from "models/QuoteDashboardIndicator";
import { PortalOrder } from "models/portalModels/PortalOrder";
import { IQuoteBionexoData } from "models/QuoteBionexo";
import { IBatchRequestResponse } from "models/Batch";
import { IUser } from "models/User";
//services
import PreOrderIndicatorServices from "services/pre-order-indicator.service";
import KeycloakPubServiceInstance from "services/keycloakpubsub.service";
import QuoteIndicatorServices from "services/quote-indicator.service";
import DomainSettingService from "services/domainSetting.service";
import QuoteBionexoServices from "services/quoteBionexo.service";
import BCGServices from "services/bcg.service";
//context
import { useDetailsQuoteContext } from "contexts/DetailsQuote";
//utils
import { subtitlesOrderDashboard } from "../components/generics/Subtitles/utils";
import { IsNullOrEmpty } from "utils/StringNullOrEmpty";
import { toastUnlock } from "../utils/Quotes";
import { handleUserRedirect } from "./utils";
//interfaces
import { OptionsDropdownProps } from "../components/QuoteStatusFilter/interface";
import { DashboardQuoteFilterProps } from "../MoneyRecordedQuotes/interface";
import { IFilter, IOrderFilter, StateType } from "./interface";
//styles
import "./styles.scss";

const QuoteDashboard: FC = () => {
  const allQuotesDashboard = process.env.REACT_APP_ALL_QUOTES_DASHBOARD;
  const bcgOnly = process.env.REACT_APP_BCG_ONLY === 'true';

  const globalFilter = useSelector((stateFilter: RootState) => stateFilter.quotesDashboard);
  const quotesTableReload = useSelector((stateTable: RootState) => stateTable.quotesTableReload);
  const quoteItemStorage = useSelector((stateStorage: RootState) => stateStorage.quoteItem) ?? { id: "" };
  const sentExpiredFilterCard = useSelector((stateFilter: RootState) => stateFilter.quotesDashboard?.sentExpiredFilter);
  const selectedIndicators = useSelector((stateFilter: RootState) =>
    stateFilter.quotesDashboard?.selectedIndicators || []
  );

  const dispatch = useDispatch();
  const location: StateType = useLocation();
  const user = useSelector((state: { user: IUser; }) => state.user);

  const isFetchingRef = useRef(false);

  const { state } = location;
  const { setOrder } = useDetailsQuoteContext();

  const [filterText, setFilterText] = useState("");
  const [tableOrder, setTableOrder] = useState("createdAt desc");
  const [currentPage, setCurrentPage] = useState(0);
  const [currentsessionPageNumber, setCurrentsessionPageNumber] = useState(0);
  const [noRefresh, setNoRefresh] = useState(false);
  const [selectedRadio, setSelectedRadio] = useState<number>(0);
  const [orderRadioSelected, setOrderRadioSelected] = useState(false);

  const [quote, setQuote] = useState<IBatchRequestResponse<IQuoteBionexoData>>();

  const [availableQuotes, setAvailableQuotes] = useState<QuoteDashboardIndicator>({
    title: "ABERTAS",
    value: "0",
    loading: false,
    error: false,
  });

  const [registerMoney, setRegisterMoney] = useState<QuoteDashboardIndicator>({
    title: "ENVIADAS",
    value: "0",
    tip: "Últimos 30 dias",
    loading: false,
    error: false,
  });

  const [pendingMoney, setPendingMoney] = useState<QuoteDashboardIndicator>({
    title: "PENDENTE MONEY",
    value: "0",
    tip: "Últimos 30 dias",
    loading: false,
    error: false,
  });

  const [orders, setOrders] = useState<QuoteDashboardIndicator>({
    title: "PEDIDOS",
    value: "0",
    tip: "Últimos 30 dias",
    loading: false,
    error: false,
  });

  const [sentAndExpired, setSentAndExpired] = useState<QuoteDashboardIndicator>({
    title: "ENVIADAS/EXPIRADAS",
    value: "0",
    tip: "Últimos 30 dias",
    loading: false,
    error: false,
  });

  const [expiredUnanswered, setExpiredUnanswered] = useState<QuoteDashboardIndicator>({
    title: "EXPIRADAS",
    value: "0",
    tip: "Últimos 30 dias",
    loading: false,
    error: false,
  });

  const pageSizeGrid = 10;
  const [isFilterUpdated, setIsFilterUptated] = useState(false);
  const [statusBionexo, setStatusBionexo] = useState<Array<string>>(globalFilter?.statusBionexo ?? ["OPEN"]);
  const [statusViveo, setStatusViveo] = useState<Array<string>>(globalFilter?.statusViveo ?? ["VALIDATE_PORTAL"]);
  const [quoteCategory, setQuoteCategory] = useState<Array<string>>(globalFilter?.quoteCategory ?? []);
  const [orderStatus, setOrderStatus] = useState<Array<string>>(globalFilter?.orderStatus ?? ["CONFIRMED", "CANCELED"]);
  const [dashboardQuoteFilter, setDashboardQuoteFilter] = useState<DashboardQuoteFilterProps>();
  const [resetSelectionKey, setResetSelectionKey] = useState(0);
  const [selectedGridOption, setSelectedGridOption] = useState<0 | 1 | 2>(0);

  const _bcgService = new BCGServices();
  const indicatorService = new QuoteIndicatorServices();
  const _domainSettingService = new DomainSettingService();
  const _quoteBionexoServices = new QuoteBionexoServices();
  const orderIndicatorService = new PreOrderIndicatorServices();

  const startDate = startOfDay(sub(new Date(), { days: 30 }));
  const endDate = endOfDay(new Date());

  const quoteSubtitle = () => <></>;
  const orderSubtitle = () => <Subtitles subtitles={subtitlesOrderDashboard} />;

  useEffect(() => {
    const { startDate, endDate }: any = globalFilter;
    if (globalFilter && startDate && endDate) {
      setIsFilterUptated(true);
    }
  }, [globalFilter]);

  useEffect(() => {
    setCurrentPage(0);
    setCurrentsessionPageNumber(0);
  }, [currentsessionPageNumber]);

  const getDashboardQuoteFilter = async () => {
    const response = await _domainSettingService.get(
      undefined,
      undefined,
      undefined,
      {
        and: [
          { identifier: { eq: "DashboardQuoteFilter" } },
          { enabled: { eq: true } },
        ],
      }
    );

    const { data } = response;
    const parseValueOptions: OptionsDropdownProps[] = JSON.parse(data[0].value);

    const viveoOptions = parseValueOptions.filter(
      (x) => x.label === "statusViveo"
    );
    const bionexoOptions = parseValueOptions.filter(
      (x) => x.label === "statusBionexo"
    );
    const categoryMafraOptions = parseValueOptions.filter(
      (x) => x.label === "quoteCategory"
    );
    const orderOptions = parseValueOptions.filter(
      (x) => x.label === "orderStatus"
    );

    setDashboardQuoteFilter({
      orderOptions,
      viveoOptions,
      bionexoOptions,
      categoryMafraOptions,
    });
  };

  useEffect(() => {
    const Currentsession = Number(
      window.sessionStorage.getItem(
        location.pathname + " DashboardCurrentPage: "
      )
    );
    setCurrentsessionPageNumber(Currentsession);
    setCurrentPage(Currentsession);
    getDashboardQuoteFilter();
  }, []);

  useEffect(() => {
    handleUserRedirect(user, history);
  }, [user]);

  useEffect(() => {
    KeycloakPubServiceInstance.subscribe(async () => {
      if (!filterText) {
        if (!globalFilter?.startDate) return;

        if (isFilterUpdated) {
          getIndicatorDetails();
          setIsFilterUptated(false);
        }
      }
      //Set in Session current Page clicked
      window.sessionStorage.setItem(
        location.pathname + " DashboardCurrentPage: ",
        currentPage.toString()
      );
    });
  }, [globalFilter, filterText, currentPage]);

  const setUseStateMainIndicator = async (
    findData: Promise<any>,
    data: QuoteDashboardIndicator,
    setData: Dispatch<SetStateAction<QuoteDashboardIndicator>>
  ) => {
    setData({ ...data, loading: true, error: false });
    findData
      .then((res) => {
        data.value = res;
        setData({ ...data, loading: false, error: false });
      })
      .catch(() => {
        setData({ ...data, loading: false, error: true });
      });
  };

  const handleSkip =
    currentsessionPageNumber === 0
      ? currentPage * 10
      : currentsessionPageNumber * 10;

  const getIndicatorsFilters = (selectedIndicators: string[]) => {
    return {
      indicatorQuoteWithAutomaticItems: selectedIndicators.includes("auto-response-indicator"),
      indicatorIsRead: selectedIndicators.includes("read-quotes-indicator"),
      indicatorUnread: selectedIndicators.includes("unread-quotes-indicator"),
      indicatorPriorityItem: selectedIndicators.includes("priority-SKUs-indicator"),
      indicatorCloseToExpiring: selectedIndicators.includes("close-to-expiring-indicator"),
    };
  };

  const getBionexoQuote = async (
    filterTextQuote?: string,
    statusBionexoQuote?: string[],
    statusViveoQuote?: string[],
    quoteCategory?: string[],
    resetCurrentPage?: boolean,
    sentExpiredFilter?: boolean
  ) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
  
    try {
      filterTextQuote = filterTextQuote?.toLowerCase().trim();
      const filterTextState = filterText?.toLowerCase().trim();
      setQuote(undefined);

      const { bu } = await dataUserBCG;

      const listStatus: Array<string> = ["cancelado", "aberto", "fechado"];

      let filter: IFilter = {
        Categories: globalFilter?.productCategories,
        StartDate: globalFilter?.startDate ?? startDate,
        EndDate: globalFilter?.endDate ?? endDate,
        Calendar: globalFilter?.filterON,
        SentExpiredFilter: sentExpiredFilter ?? false,
        BusinessUnitIdentifier: bcgOnly || IsNullOrEmpty(bu) ? globalFilter?.subsidiary?.join() : bu,
        SellerCodes: globalFilter?.sellerId,
        Status: listStatus.includes(filterTextQuote || "") ? filterTextQuote : "aberto",
        StatusBionexo: globalFilter?.statusBionexo ?? statusBionexo,
        StatusViveo: globalFilter?.statusViveo ?? statusViveo,
        CategoryQuoteMafra: globalFilter?.quoteCategory ?? quoteCategory,
        Description: filterTextState,
        Code: filterTextState,
        BuyerName: filterTextState,
        CompanyCrmCustomerCode: filterTextState,
        Name: filterTextState,
        Top: 10,
        Skip: resetCurrentPage ? 0 : handleSkip,
      };

      const indicatorsFilters = getIndicatorsFilters(selectedIndicators);
      filter = {
        ...filter,
        ...indicatorsFilters,
      };

      if (resetCurrentPage) {
        setNoRefresh(true);
        setCurrentPage(0);
      }

      if (
        !orderRadioSelected &&
        statusBionexo.length === 0 &&
        statusViveo.length === 0 &&
        statusBionexoQuote?.length === 0 &&
        statusViveoQuote?.length === 0 &&
        quoteCategory?.length === 0
      ) {
        setStatusBionexo(["OPEN", "SENT", "UPDATED"]);
        setStatusViveo(["VALIDATE_PORTAL", "PENDING_MONEY", "ANSWERED_PORTAL"]);
      }

      if (statusBionexoQuote) {
        filter = {
          ...filter,
          ...indicatorsFilters,
          StatusBionexo: statusBionexoQuote,
        };
      }

      if (statusViveoQuote) {
        filter = {
          ...filter,
          ...indicatorsFilters,
          StatusViveo: statusViveoQuote,
        };
      }

      if (quoteCategory) {
        filter = {
          ...filter,
          ...indicatorsFilters,
          CategoryQuoteMafra: quoteCategory,
        };
      }

      if (filterTextQuote) {
        filter = {
          ...filter,
          ...indicatorsFilters,
          Code: filterTextQuote,
          BuyerName: filterTextQuote,
          CompanyCrmCustomerCode: filterTextQuote,
          Name: filterTextQuote,
          Description: filterTextQuote,
        };
      }

      const result = allQuotesDashboard
        ? await _quoteBionexoServices.getQuotationsDashboard(filter)
        : await _quoteBionexoServices.get(filter);

      if (result.data) {
        const dataMapping: IQuoteBionexoData[] = [];
        const { error, data, totalPages, pageSize, total } = result;

        data?.forEach((quoteBionexo: any) => {
          if (quoteBionexo.buyer)
            quoteBionexo.buyerName = quoteBionexo.buyer.name;

          dataMapping.push(quoteBionexo);
        });

        setQuote({
          error,
          data: dataMapping,
          totalPages,
          pageSize,
          total,
        });
        setNoRefresh(false);
        dispatch({ type: "SET_QUOTE_ITEM", data: { id: "" } });
      } else {
        setNoRefresh(false);
        setQuote({
          error: true,
          pageSize: 0,
          total: 0,
          totalPages: 0,
          data: undefined,
        });
      } 
    }catch (error) {
      console.error("Error fetching quotes:", error);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const getPreOrder = async (
    orderSearchText?: string,
    status?: string[],
    categories?: string[],
    resetCurrentPage?: boolean,
    gridOprion?: 0 | 1 | 2
  ) => {
    orderSearchText = orderSearchText?.toUpperCase().trim();
    const filterTextState = filterText?.toUpperCase().trim();
    setOrder(Object());

    const sortBy = tableOrder.split(" ")[0];
    const sortDirection = tableOrder.split(" ")[1];

    const { bu } = await dataUserBCG;

    let orderFilter: IOrderFilter = {
      SearchText: filterTextState,
      Status: globalFilter?.orderStatus ?? orderStatus,
      BusinessUnitIdentifier: bcgOnly || IsNullOrEmpty(bu) ? globalFilter?.subsidiary?.join() : bu,
      SellerCodes: globalFilter?.sellerId,
      StartDate: globalFilter?.startDate ?? startDate,
      EndDate: globalFilter?.endDate ?? endDate,
      sortBy: sortBy,
      sortDirection: sortDirection,
      Top: 10,
      Skip: resetCurrentPage ? 0 : handleSkip,
      FilterType: gridOprion,
    };

    if (resetCurrentPage) {
      setNoRefresh(true);
      setCurrentPage(0);
    }

    if (orderStatus.length === 0 && orderRadioSelected) {
      setOrderStatus(["CONFIRMED", "CANCELED"]);
    }

    if (status) {
      orderFilter = {
        ...orderFilter,
        Status: status,
      };
    }

    if (categories) {
      orderFilter = {
        ...orderFilter,
        Categories: categories,
      };
    }

    if (orderSearchText) {
      orderFilter = {
        ...orderFilter,
        SearchText: orderSearchText,
      };
    }

    const result = await _quoteBionexoServices.getPreOrder(orderFilter);
    if (result.data) {
      const orderMapping: PortalOrder[] = [];
      const { error, data, totalPages, pageSize, total } = result;

      data?.forEach((order: any) => {
        orderMapping.push(order);
      });

      setOrder({
        error,
        data: orderMapping,
        totalPages,
        pageSize,
        total,
      });
      setNoRefresh(false);
    } else {
      setNoRefresh(false);
      setOrder({
        error: true,
        pageSize: 0,
        total: 0,
        totalPages: 0,
        data: undefined,
      });
    }
  };

  useEffect(() => {
    if (!noRefresh) {
      if (orderRadioSelected) {
        getPreOrder(filterText, orderStatus, quoteCategory, false, selectedGridOption);
      } else if (!isFetchingRef.current) {
        getBionexoQuote('', statusBionexo, statusViveo, quoteCategory, false, sentExpiredFilterCard);  
      }
    }
  }, [currentPage, globalFilter, tableOrder]);

  const refreshItem = useCallback((
    field: string,
    value?: any,
    sequenceNumber?: string,
    filterDetail?: string,
    valueFilterDetail?: any
  ) => {
    if (!quote?.data) return;

    const fields = field.split(".");

    let index: number = quote?.data.findIndex((item: any) => item.id == sequenceNumber);

    let data = Object.assign(quote?.data) as Array<any>;

    let item = {};

    type field0 = keyof (typeof data)[0];

    if (fields.length == 1) {
      let detailItemObj: any = { [fields[0] as field0]: value };
      item = { ...data[index], ...detailItemObj };
    } else if (fields.length == 2) {
      const itemDetail = data[index][fields[0] as field0];
      if (!itemDetail.length) return;

      const indexDetail = itemDetail.findIndex(
        (item: any) => item[filterDetail!] == valueFilterDetail
      );

      if (indexDetail < 0) return;

      let detailItemObj = { [fields[1]]: value };
      itemDetail[indexDetail] = {
        ...itemDetail[indexDetail],
        ...detailItemObj,
      };

      item = { ...data[index], [fields[0]]: itemDetail };
    }

    data[index] = item;

    setQuote({ ...quote, data: data });
    },[quote]
  );

  const getIndicatorDetails =
    async () => {
      const { bu } = await dataUserBCG;

      const businessUnitIdentifier = bcgOnly || IsNullOrEmpty(bu) ? globalFilter?.subsidiary?.join() : bu?.split(",");
      const sellerCodes = globalFilter?.sellerId;
      const calendarFilter = globalFilter?.filterON;
      const sentExpiredFilter = true;

      await setUseStateMainIndicator(
        indicatorService.AvailableForQuote(
          businessUnitIdentifier,
          sellerCodes,
          globalFilter?.productCategories,
          [0],
          [0],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate,
          calendarFilter
        ),
        availableQuotes,
        setAvailableQuotes
      );

      await setUseStateMainIndicator(
        indicatorService.AvailableForQuote(
          businessUnitIdentifier,
          sellerCodes,
          globalFilter?.productCategories,
          [0],
          [0],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate,
          calendarFilter
        ),
        availableQuotes,
        setAvailableQuotes
      );

      await setUseStateMainIndicator(
        indicatorService.DefaultIndicator(
          businessUnitIdentifier,
          sellerCodes,
          globalFilter?.productCategories,
          [0, 1, 2, 3, 4, 6, 8],
          [1, 2, 3, 5, 6, 11, 13],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate,
          calendarFilter
        ),
        registerMoney,
        setRegisterMoney
      );

      await setUseStateMainIndicator(
        indicatorService.DefaultIndicator(
          businessUnitIdentifier,
          sellerCodes,
          globalFilter?.productCategories,
          [0, 2, 4, 8],
          [1],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate,
          calendarFilter
        ),
        pendingMoney,
        setPendingMoney
      );

      await setUseStateMainIndicator(
        orderIndicatorService.GetTotalPreOrders(
          businessUnitIdentifier,
          sellerCodes,
          [0, 1],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate
        ),
        orders,
        setOrders
      );

      await setUseStateMainIndicator(
        indicatorService.DefaultIndicator(
          businessUnitIdentifier,
          sellerCodes,
          globalFilter?.productCategories,
          [0, 1, 2, 3, 4, 6, 8],
          [1, 2, 3, 5, 6, 11, 13],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate,
          calendarFilter,
          sentExpiredFilter
        ),
        sentAndExpired,
        setSentAndExpired
      );

      await setUseStateMainIndicator(
        indicatorService.DefaultIndicator(
          businessUnitIdentifier,
          sellerCodes,
          globalFilter?.productCategories,
          [2],
          [7],
          globalFilter?.startDate ?? startDate,
          globalFilter?.endDate ?? endDate,
          calendarFilter
        ),
        expiredUnanswered,
        setExpiredUnanswered
      );
    };

  useEffect(() => { 
    if(!filterText) { 
      getIndicatorDetails();
      setIsFilterUptated(false);
    }
  }, [globalFilter]);

  useEffect(() => {
    if (quotesTableReload?.reload && !orderRadioSelected) {
      dispatch({ type: "RELOAD_TABLE_QUOTES", data: { reload: false } });
      getBionexoQuote();
    }
  }, [quotesTableReload]);

  useEffect(() => {
    if (
      !IsNullOrEmpty(quoteItemStorage.id) &&
      quoteItemStorage.blockedQuote &&
      !state
    ) {
      toastUnlock(quoteItemStorage, dispatch, undefined, getBionexoQuote);
    }
  }, [quoteItemStorage, state]);

  const updateFilter = (filters?: any) => {
    setCurrentPage(0);
    setIsFilterUptated(true);
    dispatch({
      type: "SET_FILTERS",
      productCategories: filters.category,
      subsidiary: filters.subsidiary,
      sellerId: filters.seller,
      startDate: globalFilter?.startDate,
      endDate: globalFilter?.endDate,
    });
  };

  useEffect(() => {
    if (!globalFilter?.startDate && !globalFilter?.endDate) {
      setCurrentPage(0);
      setCurrentsessionPageNumber(0);
      window.sessionStorage.setItem(location.pathname + " DashboardCurrentPage: ", "0");
    } else if (globalFilter?.startDate || globalFilter?.endDate) {
      setCurrentPage(0);
      setCurrentsessionPageNumber(0);
      window.sessionStorage.setItem(location.pathname + " DashboardCurrentPage: ", "0");
    }
  }, [globalFilter]);

  const dataUserBCG = useMemo(async () => {
    if (IsNullOrEmpty(user.representative)) {
      const response = await _bcgService.getUserByUserName(user.username ?? "");

      if (response.status === 200) {
        dispatch({ type: 'SET_REPRESENTATIVE_USER', buBCG: response.data.identifier, isBCGUser: true });
        return { bu: response.data.identifier };
      } else {
        dispatch({ type: 'SET_REPRESENTATIVE_USER', isBCGUser: false, buBCG: "" });
        return { bu: "" };
      }
    } else
      return { bu: user.buBCG };
  }, []);

  const resetFiltersToDefault = async () => {
    const defaultStatusBionexo = ["OPEN", "SENT", "UPDATED"];
    const defaultStatusViveo = ["VALIDATE_PORTAL", "PENDING_MONEY", "ANSWERED_PORTAL"];

    setOrderRadioSelected(false);
    setSelectedRadio(0);
    setSelectedGridOption(0);
  
    setStatusBionexo(defaultStatusBionexo);
    setStatusViveo(defaultStatusViveo);

    setCurrentPage(0);

    if (resetSelectionKey) {
      dispatch(Actions.setSelectedFilterCard(null));
      dispatch(Actions.setSentExpiredFilter(false));
    }

    setResetSelectionKey((prevKey) => prevKey + 1);
  };

  return (
    <>
      <QuotePanel
        orders={orders}
        setOrders={setOrders}
        getPreOrder={getPreOrder}
        pendingMoney={pendingMoney}
        updateFilter={updateFilter}
        registerMoney={registerMoney}
        sentAndExpired={sentAndExpired}
        setStatusViveo={setStatusViveo}
        setOrderStatus={setOrderStatus}
        availableQuotes={availableQuotes}
        getBionexoQuote={getBionexoQuote}
        setPendingMoney={setPendingMoney}
        setSelectedRadio={setSelectedRadio}
        setRegisterMoney={setRegisterMoney}
        setStatusBionexo={setStatusBionexo}
        setQuoteCategory={setQuoteCategory}
        expiredUnanswered={expiredUnanswered}
        setOrderRadioSelected={setOrderRadioSelected}
        setAvailableQuotes={setAvailableQuotes}
        setExpiredUnanswered={setExpiredUnanswered}
        setUseStateMainIndicator={setUseStateMainIndicator}
        setSentAndExpired={setSentAndExpired}
        resetCardSelection={resetSelectionKey}
      />

      <Row className="flex-wrap pt-md-3 pb-3 pl-sm-0 align-center">
        <HeaderGrid
          filterText={filterText}
          selectedRadio={selectedRadio}
          statusViveo={statusViveo}
          orderStatus={orderStatus}
          getPreOrder={getPreOrder}
          statusBionexo={statusBionexo}
          quoteCategory={quoteCategory}
          setFilterText={setFilterText}
          setCurrentPage={setCurrentPage}
          setStatusViveo={setStatusViveo}
          setOrderStatus={setOrderStatus}
          getBionexoQuote={getBionexoQuote}
          setStatusBionexo={setStatusBionexo}
          setQuoteCategory={setQuoteCategory}
          setSelectedRadio={setSelectedRadio}
          resetFilters={resetFiltersToDefault}
          orderRadioSelected={orderRadioSelected}
          dashboardQuoteFilter={dashboardQuoteFilter}
          setOrderRadioSelected={setOrderRadioSelected}
          setSelectedGridOption={setSelectedGridOption}
          selectedGridOption={selectedGridOption}
          setNoRefresh={setNoRefresh}
        />

        <GridQuotes
          quote={quote}
          tableOrder={tableOrder}
          refreshItem={refreshItem}
          getPreOrder={getPreOrder}
          currentPage={currentPage}
          pageSizeGrid={pageSizeGrid}
          setTableOrder={setTableOrder}
          setCurrentPage={setCurrentPage}
          getBionexoQuote={getBionexoQuote}
          orderRadioSelected={orderRadioSelected}
        />

        <VisibilityConditional
          condition={!orderRadioSelected}
          firstOption={quoteSubtitle}
          secondOption={orderSubtitle}
        />
      </Row>
    </>
  );
};

export default QuoteDashboard;
